import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { enrollApi } from '@/pages/dashboard/StudentEnrollmentPage';

export const BulkEnrolDialog = ({ open, onOpenChange, schoolId, schoolName, classes, onDone }) => {
  const [year, setYear] = useState('');
  const [classId, setClassId] = useState('');
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);

  const years = [...new Set(classes.map((c) => c.year))].sort((a, b) => a - b);
  const yearClasses = classes.filter((c) => String(c.year) === String(year));

  useEffect(() => {
    if (!open) { setYear(''); setClassId(''); setCount(null); return; }
  }, [open]);

  useEffect(() => {
    if (!year || !schoolId) { setCount(null); return; }
    enrollApi(`/students?school_id=${schoolId}&year=${year}`).then((s) => setCount(s.length)).catch(() => setCount(null));
  }, [year, schoolId]);

  const run = async () => {
    setBusy(true);
    try {
      const students = await enrollApi(`/students?school_id=${schoolId}&year=${year}`);
      const r = await enrollApi('/bulk-assign', { method: 'POST', body: JSON.stringify({ class_id: classId, student_ids: students.map((s) => s.id) }) });
      toast.success(`${r.added} student${r.added === 1 ? '' : 's'} enrolled`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-enrol-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Enrol a whole year group</DialogTitle>
          <DialogDescription>Put every Year-group student at {schoolName || 'this school'} into one subject class. Students already enrolled are skipped.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <select value={year} onChange={(e) => { setYear(e.target.value); setClassId(''); }} className="w-full h-10 border rounded-md px-3 bg-background text-sm" data-testid="bulk-year-select">
            <option value="">Choose year group…</option>
            {years.map((y) => <option key={y} value={y}>Year {y}{count !== null && String(y) === String(year) ? ` — ${count} students` : ''}</option>)}
          </select>
          <select value={classId} onChange={(e) => setClassId(e.target.value)} disabled={!year} className="w-full h-10 border rounded-md px-3 bg-background text-sm" data-testid="bulk-class-select">
            <option value="">Choose subject class…</option>
            {yearClasses.map((c) => <option key={c.id} value={c.id}>{c.name}{c.teacher ? ` — ${c.teacher}` : ''} ({c.student_count} enrolled)</option>)}
          </select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={run} disabled={!classId || busy || !count} className="bg-teal-700 hover:bg-teal-800" data-testid="bulk-enrol-run">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Enrol {count ?? ''} students
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
